import React, { useEffect, useMemo, useRef, useContext, useState } from 'react'
import { withRouter, Route, Switch } from 'react-router-dom'
import 'react-chartjs-2'
import { connect } from 'react-redux'

import AreaChart from './components/AreaChart'
import HourlyChart from './components/HourlyChart'
import AreaChartButton from './components/AreaChartButton'
import HourlyChartButton from './components/HourlyChartButton'
import ChartContainer from '../../uiKit/ChartContainer'
import NamedCounter from './components/NamedCounter'
import ChannelsCircleChart from './components/ChannelsCircleChart'
import ChannelsChartButton from './components/ChannelsChartButton'
import CarecoAdminDashboard from './components/CarecoAdminDashboard'
import Headline from 'uiKit/texts/Headline'
import { DateControl, DATE_PICKER_FREQUENCIES } from 'uiKit/DateControlNew/DateControl'
import { DateControlProvider, DateControlContext } from 'contexts/DateControlContext'
import moment from 'moment'

import { dashboardRoutes } from 'routes/dashboardRoutes'

import { isObjectEmpty } from '../../helpers/isObjectEmpty'

import * as S from './Dashboard.style'

import {
  areaChartTooltipText,
  channelsChartTooltipText,
  hourlyChartContainerTitle,
  hourlyChartTooltipText,
} from './constants/chartsTexts'

import classes from './styles.module.scss'
import { usersChartDataType } from 'models/DashboardTypes'
import { setBrowserTabTitle } from '../../helpers/setBrowserTabTitle'
import { canViewCarecoDashboard } from '../../security'
import { isQcsBotPayload } from '../../constants/qcs'
import { isProfessionalsNavAllowed } from 'tabs/home/api/usageLimits'
import QcsDashboard from './components/QcsDashboard'
import { exportDashboardXlsx } from './api/dashboard'
import { getDashboardDateApiParams } from './helpers/dashboardDateParams'
import LoaderOverlay from 'uiKit/loaders/loaderOverlay'

interface Props {
  usersStatistics: usersChartDataType
  supportStatistics: any
  popularHoursStatistics: any
  match: any
  activeBot: any
  location: any
  adminUser: any
}

const StandardDashboardCharts: React.FC<{
  botId: string
  platformsNumber: boolean | undefined
  usersStatistics: usersChartDataType
  activeUsersNumber: number
  popularHoursStatistics: any
}> = ({
  botId,
  platformsNumber,
  usersStatistics,
  activeUsersNumber,
  popularHoursStatistics,
}) => (
  <>
    <div className={classes.row}>
      <ChartContainer
        title="Active users"
        tooltipText={areaChartTooltipText}
        newUsers={!platformsNumber && usersStatistics?.newUsers}
        activeUsers={!platformsNumber && activeUsersNumber}
        button={usersStatistics && <AreaChartButton />}>
        <AreaChart isMultipleChannels={!!platformsNumber} botId={botId} />
      </ChartContainer>

      {platformsNumber && (
        <div className={classes.flexBlockColumn}>
          <div className={classes.flexBlock}>
            <NamedCounter value={activeUsersNumber} title="Active users" />
            <NamedCounter value={usersStatistics?.newUsers} title="New users" />
          </div>

          <ChartContainer
            title="Users per channel"
            tooltipText={channelsChartTooltipText}
            button={usersStatistics && <ChannelsChartButton />}>
            <ChannelsCircleChart />
          </ChartContainer>
        </div>
      )}
    </div>

    <div className={classes.row}>
      <ChartContainer
        title={hourlyChartContainerTitle}
        tooltipText={hourlyChartTooltipText}
        button={popularHoursStatistics && <HourlyChartButton />}>
        <HourlyChart botId={botId} />
      </ChartContainer>
    </div>
  </>
)

const Dashboard: React.FC<Props> = ({
  usersStatistics,
  supportStatistics,
  popularHoursStatistics,
  match,
  activeBot,
  location,
  adminUser,
}) => {
  const { botId } = match.params
  const containerRef = useRef(null)

  useEffect(() => {
    containerRef.current.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    setBrowserTabTitle(`${activeBot?.name} - Dashboard`)
  }, [activeBot])

  const platformsNumber = useMemo(() => {
    if (usersStatistics?.points && !isObjectEmpty(usersStatistics?.points)) {
      return Object.values(usersStatistics?.points)[0]?.map(({ platform }) => platform)?.length > 2
    }
  }, [usersStatistics?.points])

  const activeUsersNumber = useMemo(() => {
    if (usersStatistics?.usersBetweenDates && !isObjectEmpty(usersStatistics?.usersBetweenDates)) {
      return Object.values(usersStatistics?.usersBetweenDates).reduce((acc, count) => acc + count, 0)
    }
    return 0
  }, [usersStatistics?.usersBetweenDates])

  const isCarecoAdmin = useMemo(
    () => canViewCarecoDashboard(botId, adminUser?.botAccesses),
    [botId, adminUser],
  )

  // Same Professionals/supervision package gate: drop deep-link route when gated.
  // CareBrain / enabled / grandfathered → helper is true → all routes kept.
  const visibleDashboardRoutes = useMemo(
    () =>
      dashboardRoutes.filter(
        ({ path }) => path !== 'supervision' || isProfessionalsNavAllowed(activeBot),
      ),
    [activeBot],
  )

  const DashboardExport: React.FC<{ hideFrequency?: boolean }> = ({ hideFrequency }) => {
    const { dateRange, frequency } = useContext(DateControlContext)
    const [isExporting, setIsExporting] = useState(false)
    const handleExport = async () => {
      try {
        setIsExporting(true)
        const dateParams = getDashboardDateApiParams(
          frequency,
          dateRange?.startDate,
          dateRange?.endDate,
          true,
        )
        const blob = await exportDashboardXlsx(botId, dateParams)
        if (!blob) return
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = dateParams.isDefault
          ? 'dashboard-default.xlsx'
          : `dashboard-${dateParams.fromDate}_to_${dateParams.toDate}.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        // noop, errors handled in API layer
      } finally {
        setIsExporting(false)
      }
    }
    return (
      <>
        <DateControl isCarecoAdmin handleExport={handleExport} hideFrequency={hideFrequency} />
        {isExporting && <LoaderOverlay />}
      </>
    )
  }

  // A QCS organisation gets the QCS-themed dashboard (a trimmed Careco layout —
  // no Chats card, no Top queried topics), regardless of the Careco check.
  // Data comes from the same Careco dashboard API; only the chrome is QCS-specific.
  if (isQcsBotPayload(activeBot)) {
    return (
      <div className={classes.container} ref={containerRef}>
        <DateControlProvider
          key={`${activeBot?.id}-qcs`}
          initialStartDate={activeBot?.createdAt ? moment(activeBot.createdAt) : undefined}
          initialFrequency={DATE_PICKER_FREQUENCIES.default}>
          <Switch>
            {visibleDashboardRoutes.map(({ path, component }, idx) => (
              <Route key={path || idx} path={`${match.url}/${path}`} component={component} />
            ))}
            <Route exact path={match.url}>
              <>
                <S.HeadlineWrap $isSticky={location.pathname === match.url}>
                  <Headline title="Dashboard" />
                  <DashboardExport hideFrequency />
                </S.HeadlineWrap>
                <QcsDashboard botId={botId} />
              </>
            </Route>
          </Switch>
        </DateControlProvider>
      </div>
    )
  }

  if (!isCarecoAdmin) {
    return (
      <div className={classes.container} ref={containerRef}>
        <DateControlProvider>
          <S.HeadlineWrap>
            <Headline title="Dashboard" />
            <DateControl />
          </S.HeadlineWrap>
          <StandardDashboardCharts
            botId={botId}
            platformsNumber={platformsNumber}
            usersStatistics={usersStatistics}
            activeUsersNumber={activeUsersNumber}
            popularHoursStatistics={popularHoursStatistics}
          />
        </DateControlProvider>
      </div>
    )
  }

  return (
    <div className={classes.container} ref={containerRef}>
      <DateControlProvider
        key={`${activeBot?.id}-careco`}
        initialStartDate={activeBot?.createdAt ? moment(activeBot.createdAt) : undefined}
        initialFrequency={DATE_PICKER_FREQUENCIES.default}>
          <Switch>
            {visibleDashboardRoutes.map(({ path, component }, idx) => (
              <Route key={path || idx} path={`${match.url}/${path}`} component={component} />
            ))}
            <Route exact path={match.url}>
              <>
                <S.HeadlineWrap $isSticky={location.pathname === match.url}>
                  <Headline title="Dashboard" />
                  <DashboardExport />
                </S.HeadlineWrap>
                <CarecoAdminDashboard botId={botId} />
              </>
            </Route>
          </Switch>
      </DateControlProvider>
    </div>
  )
}

const mapStateToProps = (state: {
  usersStatistics: usersChartDataType
  supportStatistics
  popularHoursStatistics
  activeBot
  adminUser
}) => ({
  usersStatistics: state.usersStatistics,
  supportStatistics: state.supportStatistics,
  popularHoursStatistics: state.popularHoursStatistics,
  activeBot: state.activeBot,
  adminUser: state.adminUser,
})

export default withRouter(connect(mapStateToProps)(Dashboard))
